import React, { useState } from "react";
import { Divider, Drawer, IconButton, Button, Box } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LogoutIcon from "@mui/icons-material/Logout";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Orders from "./Orders";
import OrderDetails from "./OrderDetails";
import UserDetails from "./UserDetails";
import Address from "./Address";
import { useAppDispatch } from "../../../State/Store";
import { logout } from "../../../State/AuthSlice";

const menu = [
  { name: "Profile", path: "/account", icon: <PersonIcon fontSize="small"/> },
  { name: "Orders", path: "/account/orders", icon: <ShoppingBagIcon fontSize="small" /> },
  { name: "Addresses", path: "/account/addresses", icon: <LocationOnIcon fontSize="small" /> },
  { name: "Saved Cards", path: "/account/saved-card", icon: <CreditCardIcon fontSize="small" /> },
  { name: "Logout", path: "/", icon: <LogoutIcon fontSize="small" /> },
];

const Account = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleClick = (item: (typeof menu)[0]) => {
    if (item.path === "/") {
      dispatch(logout(navigate));
    } else {
      navigate(item.path);
    }
    setMobileOpen(false); // Smoothly close drawer on mobile navigation
  };

  // Compute active navigation title for mobile header bar
  const activeMenuItem = menu.find((item) =>
    item.path === "/account"
      ? location.pathname === "/account"
      : location.pathname.startsWith(item.path)
  );

  const menuContent = (
    <div className="py-2 h-full flex flex-col justify-between">
      <div className="space-y-1">
        <div className="px-5 py-3 font-semibold text-lg text-gray-700 hidden lg:block">
          Account Settings
        </div>
        <Divider className="hidden lg:block mb-3" />
        {menu.map((item) => {
          const isActive =
            item.path === "/account"
              ? location.pathname === "/account"
              : location.pathname.startsWith(item.path);

          return (
            <div
              onClick={() => handleClick(item)}
              key={item.name}
              className={`flex items-center gap-3 py-3 px-5 mx-2 rounded-lg cursor-pointer transition-colors duration-150 ${
                isActive
                  ? "bg-primary-color text-white font-medium shadow-sm"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              {item.icon}
              <p className="capitalize text-sm">{item.name}</p>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="px-4 sm:px-8 lg:px-32 min-h-[85vh] mt-4 lg:mt-8">
      {/* Mobile Top Header Bar with Menu Drawer Trigger */}
      <div className="lg:hidden flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200 mb-4">
        <div className="flex items-center gap-2">
          <Button
            onClick={handleDrawerToggle}
            startIcon={<MenuIcon />}
            variant="outlined"
            size="small"
            color="primary"
          >
            Menu
          </Button>
          <span className="text-sm font-semibold capitalize text-gray-700">
            {activeMenuItem?.name || "My Account"}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 min-h-[75vh]">
        {/* Desktop Permanent Sidebar Navigation */}
        <section className="hidden lg:block col-span-1 border-r border-gray-200 pr-4">
          {menuContent}
        </section>

        {/* Mobile Slide-Over Drawer Navigation */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }} // Better mobile performance
          sx={{
            display: { xs: "block", lg: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: 280,
              paddingTop: 1,
            },
          }}
        >
          <Box className="flex items-center justify-between px-4 py-2 border-b">
            <span className="font-semibold text-gray-800">Account Menu</span>
            <IconButton onClick={handleDrawerToggle} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          {menuContent}
        </Drawer>

        {/* Content Body Area */}
        <section className="col-span-1 lg:col-span-3 lg:pl-4">
          <Routes>
            <Route path="/" element={<UserDetails />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/order/:orderId/:orderItemId" element={<OrderDetails />} />
            <Route path="/addresses" element={<Address />} />
          </Routes>
        </section>
      </div>
    </div>
  );
};

export default Account;